import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { fetchFeeds, discoverFeeds, refreshNews, KIND_LABELS } from "../../lib/newsApi";
import { timeAgo } from "../../lib/tone";

const Discover = () => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    setRes(null);
    try { setRes(await discoverFeeds(url)); } catch (err) { toast.error(err.response?.data?.detail || "Couldn't check that site"); }
    setBusy(false);
  };
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="rss-discover">
      <h3 className="font-heading font-semibold text-slate-900">Auto-discover feeds</h3>
      <p className="mt-1 text-sm text-slate-500">Enter any news website to find its RSS or Atom feeds.</p>
      <form onSubmit={go} className="mt-3 flex gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="e.g. thenarwhal.ca" maxLength={300} required
          className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm" data-testid="rss-discover-input" />
        <button disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-sky-900 px-5 text-sm text-white disabled:opacity-60" data-testid="rss-discover-btn"><Search size={15} />{busy ? "Checking…" : "Discover"}</button>
      </form>
      {res && (
        <ul className="mt-4 space-y-2 text-sm" data-testid="rss-discover-results">
          {res.feeds.length === 0 && <li className="text-slate-500">No feeds found on {res.url}.</li>}
          {res.feeds.map((f) => (
            <li key={f.url} className="rounded-xl bg-slate-50 p-3"><p className="font-medium text-slate-900">{f.title || f.url}</p>
              <p className="break-all font-mono text-xs text-slate-500">{f.url}</p><p className="mt-1 text-xs text-slate-600">{f.format?.toUpperCase()} · {f.entries} items · latest: {f.latest}</p></li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-slate-500">Discovered feeds aren't added to the shared dashboard automatically, so the balance between mainstream and independent sources stays intact.</p>
    </div>
  );
};

export const RssView = ({ onRefreshed }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => fetchFeeds().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    try { const r = await refreshNews(); toast.success(r.cached ? "Feeds were refreshed moments ago" : `Refreshed · ${r.refreshed} items read`); await load(); onRefreshed(); } catch { toast.error("Refresh failed"); }
    setBusy(false);
  };
  return (
    <div className="space-y-6" data-testid="rss-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="font-serif text-2xl text-slate-900">Feed management</h2>
          <p className="mt-1 text-sm text-slate-500" data-testid="rss-health">{data ? `${data.healthy} of ${data.total} feeds healthy · RSS & Atom · auto-refresh every ${data.refresh_minutes} minutes · last run ${data.last_refresh ? timeAgo(data.last_refresh) : "–"}` : "Loading…"}</p></div>
        <button onClick={refresh} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 disabled:opacity-60" data-testid="rss-refresh-btn"><RefreshCw size={15} className={busy ? "animate-spin" : ""} />Refresh now</button>
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="space-y-6">
          <Discover />
          <div className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="social-platforms">
            <h3 className="font-heading font-semibold text-slate-900">Social monitoring</h3>
            <ul className="mt-3 space-y-2 text-sm">{data?.social_platforms.map((p) => (
              <li key={p.platform} className="flex items-start gap-2">{p.status === "connected" ? <CheckCircle2 size={16} className="mt-0.5 text-emerald-600" /> : <XCircle size={16} className="mt-0.5 text-slate-400" />}
                <span><span className="font-medium text-slate-800">{p.platform}</span> <span className="text-slate-500">— {p.note}</span></span></li>))}
            </ul>
          </div>
        </div>
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <div className="max-h-[640px] overflow-auto">
            <table className="w-full text-left text-sm" data-testid="rss-feed-table">
              <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-3">Feed</th><th className="p-3">Type</th><th className="p-3">Items</th><th className="p-3">Status</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data?.feeds.map((f) => (
                  <tr key={f.url}>
                    <td className="p-3"><p className="font-medium text-slate-800">{f.outlet}</p><p className="max-w-[260px] truncate font-mono text-[11px] text-slate-400" title={f.url}>{f.via ? `via ${f.via}` : f.url}</p></td>
                    <td className="p-3 text-xs text-slate-600">{KIND_LABELS[f.kind]}<br /><span className="text-slate-400">{f.format}</span></td>
                    <td className="p-3 font-mono text-xs text-slate-600">{f.entries ?? "–"}</td>
                    <td className="p-3">{f.ok ? <span className="text-xs text-emerald-700">OK</span> : <span className="text-xs text-rose-600" title={f.error}>{f.ok === false ? "Error" : "Pending"}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
